import React, { useState, useEffect, useCallback } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  getFAQs,
  addFAQ,
  updateFAQ,
  updateFAQStatus,
  deleteFAQ
} from '../../../services/adminService';
import Loader from '../../../components/common/Loader';
import NoData from '../../../components/common/NoData';
import { usePermissions } from '../../../context/PermissionsContext';

function FAQ() {
  const { permissions } = usePermissions();
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFaqData, setDeleteFaqData] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10
  });
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
  });
  const [formErrors, setFormErrors] = useState({
    question: '',
    answer: ''
  });

  // Constants for validation
  const MAX_QUESTION_LENGTH = 200;
  const MAX_ANSWER_LENGTH = 1000;
  const MIN_QUESTION_LENGTH = 10;
  const MIN_ANSWER_LENGTH = 20;

  // Validation function
  const validateForm = () => {
    let isValid = true;
    const errors = {
      question: '',
      answer: ''
    };

    // Question validation
    if (!formData.question.trim()) {
      errors.question = 'Question is required';
      isValid = false;
    } else if (formData.question.trim().length < MIN_QUESTION_LENGTH) {
      errors.question = `Question must be at least ${MIN_QUESTION_LENGTH} characters`;
      isValid = false;
    } else if (formData.question.trim().length > MAX_QUESTION_LENGTH) {
      errors.question = `Question cannot exceed ${MAX_QUESTION_LENGTH} characters`;
      isValid = false;
    }

    // Answer validation
    if (!formData.answer.trim()) {
      errors.answer = 'Answer is required';
      isValid = false;
    } else if (formData.answer.trim().length < MIN_ANSWER_LENGTH) {
      errors.answer = `Answer must be at least ${MIN_ANSWER_LENGTH} characters`;
      isValid = false;
    } else if (formData.answer.trim().length > MAX_ANSWER_LENGTH) {
      errors.answer = `Answer cannot exceed ${MAX_ANSWER_LENGTH} characters`;
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Fetch FAQ data
  const fetchFAQData = useCallback(async () => {
    try {
      setTableLoading(true);
      const response = await getFAQs({
        page: pagination.currentPage,
        limit: pagination.recordsPerPage,
        search: search
      });

      if (response.success) {
        setFaqData(response.data.records);
        setPagination(response.data.pagination);
      } else {
        toast.error('Failed to fetch FAQ data');
      }
    } catch (error) {
      console.error('Error fetching FAQ data:', error);
      toast.error('Error fetching FAQ data');
    } finally {
      setTimeout(() => {
        setTableLoading(false);
      }, 500);
    }
  }, [pagination.currentPage, pagination.recordsPerPage, search]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchFAQData();
  }, [fetchFAQData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error when user starts typing
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    // Prevent typing if max length is reached
    if (
      (name === 'question' && value.length <= MAX_QUESTION_LENGTH) ||
      (name === 'answer' && value.length <= MAX_ANSWER_LENGTH)
    ) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Check permissions based on mode
    if (isEditMode && !permissions.settings_faq_edit) {
      toast.warning("You don't have permission to edit FAQs");
      return;
    }
    if (!isEditMode && !permissions.settings_faq_create) {
      toast.warning("You don't have permission to create FAQs");
      return;
    }

    try {
      setLoading(true);
      setTableLoading(true);

      if (isEditMode) {
        const response = await updateFAQ(editingId, {
          ...formData,
          question: formData.question.trim(),
          answer: formData.answer.trim()
        });
        if (response.success) {
          toast.success('FAQ updated successfully');
          await fetchFAQData();
        } else {
          toast.error(response.message || 'Failed to update FAQ');
        }
      } else {
        const response = await addFAQ({
          question: formData.question.trim(),
          answer: formData.answer.trim()
        });
        if (response.success) {
          toast.success('FAQ added successfully');
          await fetchFAQData();
        } else {
          toast.error(response.message || 'Failed to add FAQ');
        }
      }

      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.message || 'Failed to submit form');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ question: '', answer: '' });
    setFormErrors({ question: '', answer: '' });
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setFormData({
      question: item.question,
      answer: item.answer
    });
    setEditingId(item.id);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!permissions.settings_faq_delete) {
      toast.warning("You don't have permission to delete FAQs");
      return;
    }

    try {
      setLoading(true);
      setTableLoading(true);
      const response = await deleteFAQ(id);
      if (response.success) {
        toast.success('FAQ deleted successfully');
        await fetchFAQData();
      } else {
        toast.error(response.message || 'Failed to delete FAQ');
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error(error.response?.data?.message || 'Error deleting FAQ');
    } finally {
      setShowDeleteModal(false);
      setDeleteFaqData(null);
      setLoading(false);
      setTableLoading(false);
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    if (!permissions.settings_faq_status) {
      toast.warning("You don't have permission to change FAQ status");
      return;
    }

    try {
      setStatusLoading(id);
      setTableLoading(true);
      const response = await updateFAQStatus(id, { status: !currentStatus });
      if (response.success) {
        toast.success(`FAQ ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        await fetchFAQData();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Error updating status');
    } finally {
      setStatusLoading(null);
      setTableLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({
      ...prev,
      recordsPerPage: newLimit,
      currentPage: 1
    }));
  };

  const handleAddNew = () => {
    resetForm();
    setShowModal(true);
  };

  const handleDeleteClick = (e, id, question) => {
    e.stopPropagation();
    setDeleteFaqData({ id, question });
    setShowDeleteModal(true);
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="px-2 py-1 rounded text-sm border border-green-500 bg-green-500/10 text-green-500 inline-flex items-center gap-1">
          <Icon icon="mdi:check-circle-outline" className="text-base" />
          Active
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded text-sm border border-red-500 bg-red-500/10 text-red-500 inline-flex items-center gap-1">
        <Icon icon="mdi:close-circle-outline" className="text-base" />
        Inactive
      </span>
    );
  };

  return (
    <>
      {/* search and add button  */}
      <div className="grid grid-cols-12 gap-4 mb-3">
        <div className="col-span-12 md:col-span-6 mt-2 flex justify-start items-center">
          <div className="relative w-full max-w-[300px]">
            <Icon
              icon="fluent:search-20-regular"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            />
            <input
              type="search"
              className="w-full pl-9 pr-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
              placeholder="Search FAQs..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="col-span-12 md:col-span-6 mt-2 flex justify-end items-center">
          <button
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm shadow-sm whitespace-nowrap w-[180px]"
            onClick={handleAddNew}
            disabled={loading || !permissions.settings_faq_create}
            title={!permissions.settings_faq_create ? "You don't have permission to create FAQs" : ""}
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <Icon icon="fluent:chat-help-24-regular" width={18} height={18} />
                <span>Add New FAQ</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* table */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden mb-3">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '800px' }}>
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">SL. No.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/3">Question</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/3">Answer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tableLoading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-center">
                    <Loader
                      caption="Loading FAQ Data..."
                      minLoadTime={500}
                      containerHeight="200px"
                    />
                  </td>
                </tr>
              ) : faqData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-center">
                    <NoData caption="No FAQs found" />
                  </td>
                </tr>
              ) : (
                faqData.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-gray-500 font-medium">
                      {(pagination.currentPage - 1) * pagination.recordsPerPage + index + 1}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{item.question}</td>
                    <td className="px-4 py-3 text-gray-600">{item.answer}</td>
                    <td className="px-4 py-3">
                      {getStatusBadge(item.is_active)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                          title={!permissions.settings_faq_edit ? "You don't have permission to edit" : "Edit FAQ"}
                          onClick={() => handleEdit(item)}
                          disabled={loading || !permissions.settings_faq_edit}
                        >
                          <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                        </button>
                        <button
                          className={`p-2 rounded-lg disabled:opacity-50 transition-colors ${
                            item.is_active
                              ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                              : "text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                          }`}
                          title={!permissions.settings_faq_status ? "You don't have permission to change status" : (item.is_active ? "Deactivate" : "Activate")}
                          onClick={() => handleStatusToggle(item.id, item.is_active)}
                          disabled={statusLoading === item.id || !permissions.settings_faq_status}
                        >
                          {statusLoading === item.id ? (
                            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" role="status" aria-hidden="true"></span>
                          ) : (
                            <Icon 
                              icon={item.is_active ? "fluent:checkmark-circle-24-regular" : "fluent:dismiss-circle-24-regular"}
                              width="18" 
                              height="18" 
                            />
                          )}
                        </button>
                        <button
                          className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                          title={!permissions.settings_faq_delete ? "You don't have permission to delete" : "Delete FAQ"}
                          onClick={(e) => handleDeleteClick(e, item.id, item.question)}
                          disabled={loading || !permissions.settings_faq_delete}
                        >
                          <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* record per page and pagination – match modern tables */}
      <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <span className="hidden sm:inline">Rows per page:</span>
          <select
            className="px-3 py-1.5 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
            value={pagination.recordsPerPage}
            onChange={handleLimitChange}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="text-gray-500">
            {pagination.totalRecords === 0
              ? 'No records'
              : `Showing ${(pagination.currentPage - 1) * pagination.recordsPerPage + 1}–${Math.min(
                  pagination.currentPage * pagination.recordsPerPage,
                  pagination.totalRecords
                )} of ${pagination.totalRecords}`}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button
            type="button"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || loading || pagination.totalPages === 0}
            title="Previous page"
          >
            <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
          </button>
          <span className="px-2 text-gray-700">
            Page <span className="font-medium">{pagination.totalPages === 0 ? 0 : pagination.currentPage}</span> of{' '}
            <span className="font-medium">{pagination.totalPages || 0}</span>
          </span>
          <button
            type="button"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages || loading || pagination.totalPages === 0}
            title="Next page"
          >
            <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" tabIndex="-1">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-4 border-b border-0 pb-0">
              <div className="flex justify-between items-center">
                <h5 className="text-lg font-semibold">
                  {isEditMode ? 'Edit FAQ' : 'Add New FAQ'}
                </h5>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-4 pt-2">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="block mb-1 font-medium">
                    Question <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.question ? 'border-red-500' : 'border-input-border'}`}
                    name="question"
                    value={formData.question}
                    onChange={handleInputChange}
                    placeholder="Enter question"
                    disabled={loading}
                  />
                  {formErrors.question && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.question}</div>
                  )}
                  <small className="text-gray-500 text-sm">
                    {formData.question.length}/{MAX_QUESTION_LENGTH} characters
                  </small>
                </div>
                <div className="mb-3">
                  <label className="block mb-1 font-medium">
                    Answer <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.answer ? 'border-red-500' : 'border-input-border'}`}
                    name="answer"
                    value={formData.answer}
                    onChange={handleInputChange}
                    placeholder="Enter answer"
                    rows="4"
                    disabled={loading}
                  />
                  {formErrors.answer && (
                    <div className="text-red-500 text-sm mt-1">{formErrors.answer}</div>
                  )}
                  <small className="text-gray-500 text-sm">
                    {formData.answer.length}/{MAX_ANSWER_LENGTH} characters
                  </small>
                </div>
                <div className="p-4 border-t flex justify-end gap-2 px-0 pb-0">
                  <button
                    type="button"
                    className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (isEditMode ? 'Update FAQ' : 'Save FAQ')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h5 className="text-lg font-semibold">Delete FAQ</h5>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteFaqData(null);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="text-center mb-3">
                <Icon icon="fluent:delete-24-regular" className="text-red-500 mb-2" width="48" height="48" />
                <h5>Are you sure?</h5>
                <p className="text-gray-500">
                  Do you really want to delete this FAQ? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                type="button"
                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteFaqData(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2"
                onClick={() => handleDelete(deleteFaqData?.id)}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" role="status" aria-hidden="true"></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                    Delete FAQ
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default FAQ
